import { useEffect, useState } from "react";
import { VLInput } from "views/uikit/FormFields/VLInput/VLInput";
import { Select, Form, Flex, Modal } from "antd";
import { useUserModalActions } from "./useUserModals";
import { USER_ROLE_OPTIONS, UserRole } from "helpers/constants/users";
import { VLModalTitle } from "views/uikit/VLModalTitle/VLModalTitle";

export interface UserData {
  id: string;
  username: string;
  role: UserRole;
}

type FormValues = {
  username: string;
  role: UserRole;
};
interface ChangeUserRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserData | null;
  onRefresh?: () => void;
}

const FORM_ID = "change-user-role-form";

const ChangeUserRoleModal = ({
  isOpen,
  onClose,
  user,
  onRefresh,
}: ChangeUserRoleModalProps) => {
  const { changeUserRole, loading, confirmAdminRoleChange } =
    useUserModalActions({
      onRefresh,
    });
  const [form] = Form.useForm<FormValues>();
  const [formValues, setFormValues] = useState<{ role: UserRole }>({
    role: UserRole.Viewer,
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      const initialValues: FormValues = {
        username: user.username,
        role: user.role,
      };
      form.setFieldsValue(initialValues);
      setFormValues({ role: user.role });
    } else {
      form.resetFields();
      form.setFieldsValue({ username: "", role: UserRole.Viewer });
      setFormValues({ role: UserRole.Viewer });
    }
  }, [user, isOpen, form]);

  const handleCancel = () => {
    form.resetFields();
    setIsConfirmOpen(false);
    onClose();
  };

  const handleSave = async (values: FormValues) => {
    if (user) {
      const performUpdate = async () => {
        await changeUserRole({ userId: user.id, role: values.role });
        handleCancel();
      };

      if (values.role === UserRole.Admin) {
        setIsConfirmOpen(true);
        confirmAdminRoleChange({
          values: { role: values.role },
          performUpdate,
          onCancel: () => setIsConfirmOpen(false),
        });
      } else {
        await performUpdate();
      }
    }
  };

  return (
    <Modal
      open={isOpen && !isConfirmOpen}
      onCancel={handleCancel}
      onOk={() => {
        form.submit();
      }}
      okText="Save"
      closable={true}
      confirmLoading={loading}
      destroyOnHidden
      okButtonProps={{ disabled: formValues.role === user?.role }}
      title={
        <VLModalTitle
          title="Change Role"
          subtitle="Select a new role to update this user's permissions"
        />
      }
    >
      <Form
        form={form}
        id={FORM_ID}
        layout="vertical"
        onFinish={handleSave}
        onValuesChange={(_, allValues) =>
          setFormValues({ role: allValues.role })
        }
        initialValues={{
          username: user?.username || "",
          role: user?.role || UserRole.Viewer,
        }}
      >
        <Flex vertical gap={16}>
          <Form.Item name="username" label="User Name">
            <VLInput disabled />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Select
              options={USER_ROLE_OPTIONS}
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            />
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  );
};

export default ChangeUserRoleModal;
